import React, { useState, useRef, useCallback, memo, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  Chip,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  MoreHoriz as MoreIcon,
  CloseOutlined,
  KeyboardArrowRight
} from '@mui/icons-material';
import { useTab, Tab } from '../../contexts/TabContext';

interface DragState {
  isDragging: boolean;
  draggedTabId: string | null;
  dragOverTabId: string | null;
}

interface TabItemProps {
  tab: Tab;
  isActive: boolean;
  totalTabs: number;
  onClose: (tabId: string, event: React.MouseEvent) => void;
  onActivate: (tabId: string) => void;
  onContextMenu: (event: React.MouseEvent, tabId: string) => void;
  dragState: DragState;
  onDragStart: (tabId: string) => void;
  onDragEnd: () => void;
  onDragOver: (tabId: string) => void;
  onDrop: (targetTabId: string) => void;
}

const TabItem = memo<TabItemProps>(({
  tab,
  isActive,
  totalTabs,
  onClose,
  onActivate,
  onContextMenu,
  dragState,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop
}) => {
  const theme = useTheme();
  const isDragging = dragState.draggedTabId === tab.id;
  const isDragOver = dragState.dragOverTabId === tab.id;

  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tab.id);
    onDragStart(tab.id);
  }, [tab.id, onDragStart]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver(tab.id);
  }, [tab.id, onDragOver]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    onDrop(tab.id);
  }, [tab.id, onDrop]);

  const handleClick = useCallback(() => {
    onActivate(tab.id);
  }, [tab.id, onActivate]);

  const handleCloseClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onClose(tab.id, e);
  }, [tab.id, onClose]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    onContextMenu(e, tab.id);
  }, [tab.id, onContextMenu]);

  return (
    <Box
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onContextMenu={handleContextMenu}
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 120,
        maxWidth: 200,
        height: 36,
        px: 1.5,
        py: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative',
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
        border: `1px solid ${theme.palette.divider}`,
        borderBottom: 'none',
        backgroundColor: isActive 
          ? theme.palette.background.paper
          : alpha(theme.palette.background.paper, 0.7),
        color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
        transition: theme.transitions.create(['background-color', 'color', 'transform'], {
          duration: theme.transitions.duration.shorter,
        }),
        transform: isDragging ? 'scale(0.95)' : 'scale(1)',
        opacity: isDragging ? 0.7 : 1,
        zIndex: isActive ? 2 : 1,
        '&:hover': {
          backgroundColor: isActive 
            ? theme.palette.background.paper
            : alpha(theme.palette.action.hover, 0.8)
        },
        '&:before': isDragOver ? {
          content: '""',
          position: 'absolute',
          left: -2,
          top: 0,
          bottom: 0,
          width: 2,
          backgroundColor: theme.palette.primary.main,
          zIndex: 10
        } : {},
        ...(isActive && {
          boxShadow: `0 -2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
          borderColor: theme.palette.divider,
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: -1,
            left: 0,
            right: 0,
            height: 2,
            backgroundColor: theme.palette.background.paper
          }
        })
      }}
    >
      {tab.icon && (
        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
          {tab.icon}
        </Box>
      )}
      
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: isActive ? 600 : 400,
          fontSize: '0.875rem'
        }}
      >
        {tab.label}
      </Typography>

      {tab.closable !== false && totalTabs > 1 && (
        <IconButton
          className="tab-close-btn"
          size="small"
          onClick={handleCloseClick}
          sx={{
            ml: 0.5,
            p: 0.25,
            opacity: isActive ? 1 : 0.7,
            transition: theme.transitions.create(['opacity', 'background-color'], {
              duration: theme.transitions.duration.shorter,
            }),
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              opacity: 1
            }
          }}
        >
          <CloseIcon sx={{ fontSize: '0.875rem' }} />
        </IconButton>
      )}
    </Box>
  );
});

TabItem.displayName = 'TabItem';

const TabBar: React.FC = () => {
  const theme = useTheme();
  const { tabs, activeTabId, navigateToTab, removeTab, closeOtherTabs, closeTabsToRight, reorderTabs } = useTab();
  
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    tabId: string;
  } | null>(null);
  
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedTabId: null,
    dragOverTabId: null
  });

  const dragStartIndex = useRef<number>(-1);

  // Keyboard shortcut for closing current tab (Ctrl+W)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'w' && activeTabId && tabs.length > 1) {
        event.preventDefault();
        removeTab(activeTabId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTabId, tabs.length, removeTab]);

  const handleTabActivate = useCallback((tabId: string) => {
    navigateToTab(tabId);
  }, [navigateToTab]);

  const handleTabClose = useCallback((tabId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    removeTab(tabId);
  }, [removeTab]);

  const handleContextMenu = useCallback((event: React.MouseEvent, tabId: string) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      tabId
    });
  }, []);

  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleCloseOthers = useCallback(() => {
    if (contextMenu) {
      closeOtherTabs(contextMenu.tabId);
      handleContextMenuClose();
    }
  }, [contextMenu, closeOtherTabs, handleContextMenuClose]);

  const handleCloseToRight = useCallback(() => {
    if (contextMenu) {
      closeTabsToRight(contextMenu.tabId);
      handleContextMenuClose();
    }
  }, [contextMenu, closeTabsToRight, handleContextMenuClose]);

  const handleDragStart = useCallback((tabId: string) => {
    const index = tabs.findIndex(tab => tab.id === tabId);
    dragStartIndex.current = index;
    setDragState({
      isDragging: true,
      draggedTabId: tabId,
      dragOverTabId: null
    });
  }, [tabs]);

  const handleDragEnd = useCallback(() => {
    setDragState({
      isDragging: false,
      draggedTabId: null,
      dragOverTabId: null
    });
    dragStartIndex.current = -1;
  }, []);

  const handleDragOver = useCallback((tabId: string) => {
    setDragState(prev => ({
      ...prev,
      dragOverTabId: tabId
    }));
  }, []);

  const handleDrop = useCallback((targetTabId: string) => {
    const targetIndex = tabs.findIndex(tab => tab.id === targetTabId);
    const sourceIndex = dragStartIndex.current;
    
    if (sourceIndex !== -1 && targetIndex !== -1 && sourceIndex !== targetIndex) {
      reorderTabs(sourceIndex, targetIndex);
    }
    
    handleDragEnd();
  }, [tabs, reorderTabs, handleDragEnd]);

  if (tabs.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          height: 40,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.background.default, 0.8),
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            flex: 1,
            overflow: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }}
        >
          {tabs.map((tab) => (
            <TabItem
              key={tab.id}
              tab={tab}
              isActive={activeTabId === tab.id}
              totalTabs={tabs.length}
              onClose={handleTabClose}
              onActivate={handleTabActivate}
              onContextMenu={handleContextMenu}
              dragState={dragState}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          ))}
        </Box>

        {tabs.length > 5 && (
          <Tooltip title="More options">
            <IconButton
              size="small"
              sx={{
                ml: 1,
                mr: 1,
                color: theme.palette.text.secondary
              }}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem 
          disabled={tabs.length <= 1}
          onClick={() => {
            if (contextMenu && tabs.length > 1) {
              removeTab(contextMenu.tabId);
              handleContextMenuClose();
            }
          }}
        >
          <ListItemIcon>
            <CloseOutlined fontSize="small" />
          </ListItemIcon>
          Close Tab
        </MenuItem>
        <MenuItem onClick={handleCloseOthers}>
          <ListItemIcon>
            <CloseOutlined fontSize="small" />
          </ListItemIcon>
          Close Other Tabs
        </MenuItem>
        <MenuItem onClick={handleCloseToRight}>
          <ListItemIcon>
            <KeyboardArrowRight fontSize="small" />
          </ListItemIcon>
          Close Tabs to the Right
        </MenuItem>
      </Menu>
    </>
  );
};

export default memo(TabBar);